/**
 * ModalDialog Utility
 * Drop-in replacement for window.alert() and window.confirm() using custom modal dialogs
 * 
 * Usage:
 *   import { ModalDialog } from '../utils/ModalDialog';
 *   
 *   // Instead of: alert('Message');
 *   ModalDialog.alert('Message');
 *   
 *   // Instead of: if (window.confirm('Question?')) { ... }
 *   if (await ModalDialog.confirm('Question?')) { ... }
 * 
 * To revert to browser dialogs, just change back to alert() and confirm()
 */

import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import AlertDialog from '../components/AlertDialog';

interface DialogState {
  isOpen: boolean;
  type: 'alert' | 'confirm';
  title?: string;
  message: string;
  dialogType?: 'info' | 'warning' | 'error';
  confirmLabel?: string;
  cancelLabel?: string;
  resolve?: (value: boolean) => void;
}

class ModalDialogManager {
  private static instance: ModalDialogManager;
  private containerElement: HTMLDivElement | null = null;
  private root: Root | null = null;
  private state: DialogState = {
    isOpen: false,
    type: 'alert',
    message: '',
  };

  private constructor() {
    // Singleton pattern
  }

  private static getInstance(): ModalDialogManager {
    if (!ModalDialogManager.instance) {
      ModalDialogManager.instance = new ModalDialogManager();
    }
    return ModalDialogManager.instance;
  }

  private ensureContainer(): void {
    if (!this.containerElement) {
      this.containerElement = document.createElement('div');
      this.containerElement.id = 'modal-dialog-container';
      document.body.appendChild(this.containerElement);
      this.root = createRoot(this.containerElement);
    }
  }

  private render(): void {
    if (!this.root) return;

    const handleClose = () => {
      this.state.isOpen = false;
      if (this.state.resolve) {
        this.state.resolve(false);
      }
      this.render();
    };

    const handleConfirm = () => {
      this.state.isOpen = false;
      if (this.state.resolve) {
        this.state.resolve(true);
      }
      this.render();
    };

    this.root.render(
      <AlertDialog
        isOpen={this.state.isOpen}
        onClose={handleClose}
        title={this.state.title}
        message={this.state.message}
        type={this.state.type === 'confirm' ? 'confirm' : this.state.dialogType || 'info'}
        confirmLabel={this.state.confirmLabel || 'OK'}
        cancelLabel={this.state.cancelLabel || 'Cancel'}
        onConfirm={this.state.type === 'confirm' ? handleConfirm : undefined}
      />
    );
  }

  /**
   * Show an alert dialog
   * @param message The message to display
   * @param title Optional title
   * @param type Optional dialog type (info, warning, error)
   * @returns Promise that resolves when dialog is closed
   */
  public static alert(
    message: string,
    title?: string,
    type?: 'info' | 'warning' | 'error'
  ): Promise<void> {
    const instance = ModalDialogManager.getInstance();
    instance.ensureContainer();

    return new Promise<void>((resolve) => {
      instance.state = {
        isOpen: true,
        type: 'alert',
        message,
        title,
        dialogType: type || 'info',
        resolve: () => resolve(),
      };
      instance.render();
    });
  }

  /**
   * Show a confirmation dialog
   * @param message The message to display
   * @param title Optional title
   * @param confirmLabel Optional confirm button label
   * @param cancelLabel Optional cancel button label
   * @returns Promise that resolves to true if confirmed, false if cancelled
   */
  public static confirm(
    message: string,
    title?: string,
    confirmLabel?: string,
    cancelLabel?: string
  ): Promise<boolean> {
    const instance = ModalDialogManager.getInstance();
    instance.ensureContainer();

    return new Promise<boolean>((resolve) => {
      instance.state = {
        isOpen: true,
        type: 'confirm',
        message,
        title,
        confirmLabel,
        cancelLabel,
        resolve,
      };
      instance.render();
    });
  }

  /**
   * Convenience methods for different alert types
   */
  public static info(message: string, title?: string): Promise<void> {
    return this.alert(message, title, 'info');
  }

  public static warning(message: string, title?: string): Promise<void> {
    return this.alert(message, title, 'warning');
  }

  public static error(message: string, title?: string): Promise<void> {
    return this.alert(message, title, 'error');
  }
}

export const ModalDialog = ModalDialogManager;
